import { ArrowLeft, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useDevice, useSiteNames } from '../../data/hooks';
import { useDeviceLive } from '../../realtime/hooks';
import {
  Card,
  ConnectivityTag,
  PageHeader,
  PresenceBadge,
  ProtocolTag,
  SeverityBadge,
} from '../../ui/components';
import { formatDateTime, formatNumber } from '../../ui/format';
import {
  DeviceControls,
  DeviceMetricsGrid,
  DeviceQueries,
  LightDial,
  MeasuredAt,
  PresenceFacts,
} from './DeviceLive';
import { CommunicationFacts, DependantsCard, LastEvidence } from './Topology';
import { DeviceReadsCard } from './DeviceReads';
import { SignalCell } from './liveCells';
import { FunctionsCard } from './FunctionsCard';
import { LightProgramCard, PROGRAM_READ } from './LightProgramCard';
import { TrafficCard } from './TrafficCard';
import { ConnectionsCard } from './ConnectionsCard';
import { DeviceFaultsCard, DeviceHistoryCard, DeviceRecordsCard } from './DeviceOpsCards';
import { useSites } from '../../data/hooks';
import { clientOf } from '../reports/ReportScope';
import { canAny } from '../../api/client';
import { useMe } from '../auth/session';
import { CorrectPositionButton, ReleaseButton } from './PositionCorrection';

function LiveHeaderBadges({ id }: { id: string }) {
  const presence = useDeviceLive(id, (d) => d?.presence);
  const alarm = useDeviceLive(id, (d) => d?.alarm);
  const alarmView = canAny(useMe().data, 'alarm.view');
  return (
    <span className="toolbar">
      <PresenceBadge presence={presence} />
      <SignalCell id={id} hideEmpty />
      {alarm && alarmView && <SeverityBadge severity={alarm} />}
    </span>
  );
}

/**
 * Layout of the device detail. Live parts subscribe to this device only. Without device.inspect (the authority
 * observer, and since 2026-10-10 the client users: site administrator, operator) only commands, telemetry, history,
 * position and overview are shown (with "Read now" for the client users, 2026-10-24); the server refuses the technical routes behind the other cards anyway.
 */
export function DeviceDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const device = useDevice(id);
  const siteNames = useSiteNames();
  const sites = useSites().data ?? [];
  // technical device data and manual reads: device.inspect (not the observer, not the client users)
  const me = useMe().data;
  const ops = canAny(me, 'device.inspect');
  const configure = ops && canAny(me, 'device.configure');
  // "Show on map" (user request 2026-10-10): superadmin, site administrator, operator — not the authority observer
  const onMap = canAny(me, 'operations.view');
  const navigate = useNavigate();
  const d = device.data;
  if (device.isLoading) return <div className="page muted">{t('common.loading')}</div>;
  if (!d) return <div className="page">{t('devices.detail.notFound')}</div>;
  return (
    <div className="page">
      <Link to="/devices" className="muted toolbar">
        <ArrowLeft size={14} /> {t('common.back')}
      </Link>
      <PageHeader
        title={d.name}
        subtitle={
          <span className="toolbar">
            <ProtocolTag protocol={d.protocol} />
            {d.model ??
              t(`catalog.deviceType.${d.deviceType}` as 'catalog.deviceType.other', {
                defaultValue: d.deviceType,
              })}
            <ConnectivityTag device={d} />
          </span>
        }
        actions={<LiveHeaderBadges id={d.id} />}
      />
      <div className="detail-layout">
        <div className="grid">
          <Card title={t('devices.detail.commands')}>
            <div className="device-control-layout">
              {d.capabilities.includes('light.set_dimmer') && <LightDial id={d.id} />}
              <div style={{ flex: 1, minWidth: 220 }}>
                <DeviceControls id={d.id} capabilities={d.capabilities} canControl={d.canControl} />
                {/* "Read now" for every account that controls the device; "Check GPS" superadmin only (inside) */}
                <DeviceQueries
                  id={d.id}
                  capabilities={d.capabilities}
                  canControl={d.canControl}
                  correction={false}
                />
                {!d.capabilities.length && (
                  <p className="muted" style={{ margin: 0 }}>
                    {t('devices.detail.commandsHint')}
                  </p>
                )}
              </div>
            </div>
          </Card>
          <Card title={t('devices.detail.telemetry')} actions={<MeasuredAt id={d.id} />}>
            <DeviceMetricsGrid id={d.id} />
          </Card>
          {ops && <DeviceReadsCard id={d.id} canControl={d.canControl} protocol={d.protocol} />}
          {/* autonomous program in human values: superadmin only (device.configure), user request 2026-10-12 */}
          {configure && d.canControl && d.capabilities.includes(PROGRAM_READ) && (
            <LightProgramCard id={d.id} />
          )}
          {ops && <FunctionsCard id={d.id} />}
          {ops && <DependantsCard id={d.id} />}
          <DeviceHistoryCard id={d.id} />
          {ops && <DeviceFaultsCard id={d.id} />}
        </div>
        <div className="grid" style={{ alignContent: 'start' }}>
          <Card
            title={t('devices.detail.overview')}
            actions={
              <span className="toolbar" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* GPS correction and release: superadmin only (U19, 2026-10-21) */}
                {d.lifecycle === 'active' && d.capabilities.includes('location.read') && (
                  <CorrectPositionButton id={d.id} name={d.name} />
                )}
                {onMap && d.lat !== undefined && d.lon !== undefined && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      // the map opens on the device, selected (its card open) and ringed, with a way back here
                      void navigate(`/map?device=${encodeURIComponent(d.id)}`, {
                        state: {
                          highlight: {
                            label: d.name,
                            points: [{ id: d.id, name: d.name, lon: d.lon!, lat: d.lat! }],
                            returnTo: { path: `/devices/${d.id}`, label: t('devices.detail.backToDevice') },
                          },
                        },
                      })
                    }
                  >
                    <MapPin size={15} /> {t('devices.detail.showOnMap')}
                  </button>
                )}
                {d.lifecycle === 'active' && (
                  <ReleaseButton
                    id={d.id}
                    name={d.name}
                    siteName={siteNames.get(d.siteId) ?? '—'}
                    onReleased={() => void navigate('/devices')}
                  />
                )}
              </span>
            }
          >
            <dl className="kv">
              <dt>{t('common.site')}</dt>
              <dd>{siteNames.get(d.siteId) ?? '—'}</dd>
              <dt>{t('common.type')}</dt>
              <dd>
                {t(`catalog.deviceType.${d.deviceType}` as 'catalog.deviceType.other', {
                  defaultValue: d.deviceType,
                })}
              </dd>
              <dt>{t('common.status')}</dt>
              <dd>
                {t(`devices.lifecycle.${d.lifecycle}` as 'devices.lifecycle.active', {
                  defaultValue: d.lifecycle,
                })}
              </dd>
              {d.upstream ? <LastEvidence id={d.id} /> : <PresenceFacts id={d.id} />}
            </dl>
          </Card>
          {ops && (
            <>
              <Card title={t('devices.detail.communication')}>
                <dl className="kv">
                  <CommunicationFacts device={d} />
                  <dt>{t('nav.gateways')}</dt>
                  <dd className="mono">{d.gatewayKey ?? '—'}</dd>
                  {!d.upstream && (
                    <>
                      <dt>{t('presence.connectedSince')}</dt>
                      <dd>{formatDateTime(d.connectedSince)}</dd>
                    </>
                  )}
                </dl>
                {d.upstream && <NoEvidenceHint id={d.id} />}
              </Card>
              <DeviceRecordsCard id={d.id} clientId={clientOf(sites, d.siteId ?? null)} />
              <TrafficCard id={d.id} />
              <ConnectionsCard id={d.id} />
              <Card title={t('devices.detail.identifiers')}>
                <dl className="kv">
                  {d.identifiers.flatMap((i) => [
                    <dt key={`${i.scheme}-k`} className="mono">
                      {i.scheme}
                    </dt>,
                    <dd key={`${i.scheme}-v`} className="mono">
                      {i.value}
                    </dd>,
                  ])}
                </dl>
              </Card>
            </>
          )}
          <Card title={t('devices.detail.location')}>
            <span className="mono">
              {d.lat !== undefined ? `${formatNumber(d.lat, 5)}, ${formatNumber(d.lon, 5)}` : '—'}
            </span>
            {d.locationSource && (
              <p className="muted small" style={{ marginBottom: 0 }}>
                {t(
                  `devices.detail.locationSource.${d.locationSource}` as 'devices.detail.locationSource.import',
                  {
                    defaultValue: d.locationSource,
                  },
                )}
              </p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/** A device reached through another one stays UNKNOWN until it is heard itself: say so while it has never been. */
function NoEvidenceHint({ id }: { id: string }) {
  const { t } = useTranslation();
  const seen = useDeviceLive(id, (x) => x?.lastSeenAt);
  if (seen) return null;
  return (
    <p className="muted small" style={{ marginBottom: 0 }}>
      {t('devices.detail.noEvidence')}
    </p>
  );
}
